<template>
  <v-container
    class="report-dashboard requests-dashboard py-0"
    fluid
  >
    <DashboardFilters @input="setCriteria" />
    <v-progress-linear
      :active="loading"
      color="secondary"
      indeterminate
    />
    <div
      v-if="dashboard"
      class="dashboard-content"
      :style="{ height: contentHeight }"
    >
      <div class="section-title">
        {{ $t('labels.mixAndOutcomes') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in overviewTiles"
          :key="tile.key"
          cols="6"
          md="3"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="8"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.requestsPerDay') }}
            </div>
            <BarChart
              :chart-data="requestsPerDayData"
              :options="stackedBarOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="chart-card request-types"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.requestTypes') }}
            </div>
            <DoughnutChart
              v-if="dashboard.requests > 0"
              :chart-data="typeMixData"
              :options="doughnutOptions"
              :styles="chartStyles"
            />
            <div
              v-else
              class="no-data grey--text"
              :style="chartStyles"
            >
              {{ $t('labels.noRecords') }}
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="chart-card outcomes"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.outcomes') }}
            </div>
            <DoughnutChart
              v-if="dashboard.requests > 0"
              :chart-data="outcomesData"
              :options="doughnutOptions"
              :styles="chartStyles"
            />
            <div
              v-else
              class="no-data grey--text"
              :style="chartStyles"
            >
              {{ $t('labels.noRecords') }}
            </div>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="8"
        >
          <v-card
            class="chart-card event-types"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.eventRequestsByType') }}
            </div>
            <HorizontalBarChart
              v-if="dashboard.byEventType.length > 0"
              :chart-data="eventTypesData"
              :options="horizontalBarOptions"
              :styles="barChartStyles(dashboard.byEventType.length)"
            />
            <div
              v-else
              class="no-data grey--text"
              :style="chartStyles"
            >
              {{ $t('labels.noRecords') }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <div class="section-title">
        {{ $t('labels.mostRequests') }}
      </div>
      <v-row dense>
        <v-col cols="12">
          <v-card
            class="chart-card"
            outlined
          >
            <div
              v-if="dashboard.topShare.staff > 0"
              class="top-share body-2 mb-2"
            >
              {{ $t('labels.topRequestersShare', {
                percent: formatNumber(dashboard.topShare.percent),
                staff: formatNumber(dashboard.topShare.staff)
              }) }}
            </div>
            <v-simple-table
              class="top-requesters"
              dense
            >
              <thead>
                <tr>
                  <th>{{ $t('labels.employeeName') }}</th>
                  <th>{{ $t('labels.homeDepartment') }}</th>
                  <th class="text-right">
                    {{ $t('labels.total') }}
                  </th>
                  <th>{{ $tc('labels.request', 2) }}</th>
                  <th class="text-right">
                    {{ $t('labels.approved') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.rejected') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.withdrawn') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.approvalRate') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in dashboard.topRequesters"
                  :key="row.userId"
                >
                  <td>{{ row.name }}</td>
                  <td>{{ row.homeDepartmentName }}</td>
                  <td class="text-right">
                    {{ formatNumber(row.total) }}
                  </td>
                  <td>
                    <v-chip
                      v-for="type in sortedTypes(row.byType)"
                      :key="type"
                      class="mr-1"
                      :color="typeColor(type)"
                      text-color="white"
                      x-small
                    >
                      {{ typeLabel(type) }} {{ row.byType[type] }}
                    </v-chip>
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.approved) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.rejected) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.withdrawn) }}
                  </td>
                  <td class="text-right">
                    {{ formatPercent(row.approvalRate) }}
                  </td>
                </tr>
                <tr v-if="dashboard.topRequesters.length === 0">
                  <td
                    class="grey--text"
                    colspan="8"
                  >
                    {{ $t('labels.noRequests') }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <div class="section-title">
        {{ $t('labels.patterns') }}
      </div>
      <v-row dense>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.requestedDaysByWeekday') }}
            </div>
            <BarChart
              :chart-data="weekdayData"
              :options="barOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.requestLeadTime') }}
            </div>
            <BarChart
              :chart-data="leadTimeData"
              :options="barOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
      </v-row>

      <div class="section-title">
        {{ $t('labels.speedAndTiming') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in timingTiles"
          :key="tile.key"
          cols="6"
          md="3"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.medianTurnaroundByType') }}
            </div>
            <BarChart
              :chart-data="turnaroundByTypeData"
              :options="barOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import _ from 'lodash';
import { BarChart, DoughnutChart, HorizontalBarChart } from '@/components/charts';
import DashboardFilters from '@/components/scheduling/reports/DashboardFilters';
import DashboardTile from '@/components/scheduling/reports/DashboardTile';
import dashboard, { LEAD_TIME_LABELS } from '@/components/scheduling/reports/dashboard';

export default {
  components: {
    BarChart,
    DashboardFilters,
    DashboardTile,
    DoughnutChart,
    HorizontalBarChart
  },
  mixins: [dashboard],
  data () {
    return {
      retrieveAction: 'report/retrieveRequestsDashboard',
      retrieveFailMessage: 'descriptions.requestsDashboardRetrieveFail'
    };
  },
  computed: {
    barOptions () {
      return {
        ...this.groupedBarOptions,
        legend: {
          display: false
        }
      };
    },
    eventTypesData () {
      const rows = this.dashboard.byEventType;
      return {
        labels: rows.map(row => row.name || this.$t('labels.unknown')),
        datasets: [this.dataset(this.$tc('labels.request', 2), this.colors.secondary, rows.map(row => row.count))]
      };
    },
    horizontalBarOptions () {
      return {
        ...this.horizontalStackedBarOptions,
        legend: {
          display: false
        }
      };
    },
    leadTimeData () {
      const rows = this.dashboard.leadTime;
      return {
        labels: rows.map(row => this.$t(`labels.${LEAD_TIME_LABELS[row.bucket]}`)),
        datasets: [this.dataset(this.$tc('labels.request', 2), this.colors.secondary, rows.map(row => row.count))]
      };
    },
    outcomeColors () {
      return {
        approved: this.colors.success,
        declined: this.colors.warning,
        expired: '#757575',
        pending: this.colors.info,
        rejected: this.colors.error,
        withdrawn: '#BDBDBD'
      };
    },
    outcomesData () {
      const rows = this.dashboard.byOutcome.filter(row => row.count > 0);
      return {
        labels: rows.map(row => this.$t(`labels.${row.outcome}`)),
        datasets: [{
          backgroundColor: rows.map(row => this.outcomeColors[row.outcome]),
          data: rows.map(row => row.count)
        }]
      };
    },
    overviewTiles () {
      const { byType, pending, requesters, requests, approvalRate } = this.dashboard;
      const mostCommon = byType[0];
      return [
        {
          key: 'requests',
          label: this.$tc('labels.request', 2),
          value: this.formatNumber(requests),
          detail: this.$t('labels.staffCount', { count: this.formatNumber(requesters) })
        },
        {
          key: 'approval-rate',
          label: this.$t('labels.approvalRate'),
          value: this.formatPercent(approvalRate),
          detail: this.$t('labels.approvalRateDetail')
        },
        {
          key: 'pending',
          label: this.$t('labels.pending'),
          value: this.formatNumber(pending),
          detail: ''
        },
        {
          key: 'most-common',
          label: this.$t('labels.mostCommonRequest'),
          value: mostCommon ? this.typeLabel(mostCommon.type) : '-',
          detail: mostCommon ? this.$tc('labels.requestCount', mostCommon.count, { count: this.formatNumber(mostCommon.count) }) : ''
        }
      ];
    },
    requestsPerDayData () {
      const { byDate, byType } = this.dashboard;
      return {
        labels: this.dayLabels(byDate),
        datasets: byType.map(({ type }) => this.dataset(
          this.typeLabel(type),
          this.typeColor(type),
          byDate.map(day => day.byType[type] || 0)
        ))
      };
    },
    timingTiles () {
      const { turnaround } = this.dashboard;
      return [
        {
          key: 'turnaround',
          label: this.$t('labels.medianTurnaround'),
          value: this.formatHours(turnaround.medianHours),
          detail: this.$t('labels.turnaroundDetail')
        },
        {
          key: 'within-day',
          label: this.$t('labels.decidedWithinDay'),
          value: this.formatPercent(turnaround.withinDayPercent),
          detail: ''
        }
      ];
    },
    turnaroundByTypeData () {
      const rows = this.dashboard.turnaround.byType;
      return {
        labels: rows.map(row => this.typeLabel(row.type)),
        datasets: [{
          backgroundColor: rows.map(row => this.typeColor(row.type)),
          data: rows.map(row => row.medianHours),
          label: this.$t('labels.medianTurnaround')
        }]
      };
    },
    typeMixData () {
      const rows = this.dashboard.byType;
      return {
        labels: rows.map(row => this.typeLabel(row.type)),
        datasets: [{
          backgroundColor: rows.map(row => this.typeColor(row.type)),
          data: rows.map(row => row.count)
        }]
      };
    },
    weekdayData () {
      const rows = this.weekdayRows(this.dashboard.byWeekday);
      return {
        labels: rows.map(row => row.name),
        datasets: [this.dataset(this.$tc('labels.request', 2), this.colors.secondary, rows.map(row => row.count || 0))]
      };
    }
  },
  methods: {
    sortedTypes (counts) {
      return _.sortBy(Object.keys(counts), [type => -counts[type], type => type]);
    },
    typeColor (type) {
      const colors = {
        event: this.colors.secondary,
        giveaway: this.colors.accent,
        reschedule: this.colors.info,
        split: this.colors.success,
        swap: this.colors.primary
      };
      return colors[type] || '#9E9E9E';
    },
    typeLabel (type) {
      switch (type) {
        case 'event':
          return this.$tc('labels.event', 1);
        case 'giveaway':
          return this.$t('labels.giveaway');
        case 'reschedule':
          return this.$t('labels.reschedule');
        case 'split':
          return this.$tc('labels.split', 1);
        case 'swap':
          return this.$t('labels.swap');
        default:
          return _.startCase(type);
      }
    }
  }
};
</script>

<style lang="scss">
@import "src/assets/sass/report-dashboard.scss";
</style>
